import Feature from 'ol/Feature';
import Map from 'ol/Map';
import { Point } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import { transform } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import { Style } from 'ol/style';
import { useEffect, useRef } from 'react';
import { useOpenLayersContext } from '../OpenLayersContext';
import { unByKey } from 'ol/Observable';

/**
 * Spring easing function - overshoots then settles
 * Based on spring physics with ~4% overshoot
 */
const springEase = (t: number): number => {
  if (t <= 0) return 0;
  if (t >= 1) return 1;

  const omega = 8;
  const zeta = 0.6;
  const decay = Math.exp(-zeta * omega * t);
  const oscillation = Math.cos(omega * Math.sqrt(1 - zeta * zeta) * t);
  return 1 - decay * oscillation;
};

/**
 * Gentle breathing pulse - subtle scale oscillation
 */
const breathePulse = (t: number): number => {
  // Slow sine wave between 0.97 and 1.03 (3% variation)
  return 1 + 0.03 * Math.sin(t * Math.PI * 2);
};

/** Read the --primary CSS variable and convert HSL to RGB */
const getPrimaryColor = (): { r: number; g: number; b: number } => {
  const fallback = { r: 0, g: 122, b: 128 }; // Default teal

  try {
    const hslValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--primary')
      .trim();

    if (!hslValue) return fallback;

    // Parse "191 100% 24%" format
    const parts = hslValue.split(/\s+/);
    if (parts.length < 3) return fallback;

    const h = parseFloat(parts[0]) / 360;
    const s = parseFloat(parts[1]) / 100;
    const l = parseFloat(parts[2]) / 100;

    // HSL to RGB conversion
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    return {
      r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
      g: Math.round(hue2rgb(p, q, h) * 255),
      b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
    };
  } catch {
    return fallback;
  }
};

export const useDrawCrosshair = (
  latitude: number | null,
  longitude: number | null,
  map: Map
) => {
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const animationRef = useRef<{
    startTime: number | null;
    isInitialAnimation: boolean;
  }>({ startTime: null, isInitialAnimation: true });

  useEffect(() => {
    if (latitude === null || longitude === null) {
      return;
    }

    const zoom = map.getView().getZoom();

    // Zoom level 15 is where we draw the ships based on their true dimensions,
    //  which causes the crosshair to be drawn at the wrong position CLASSIC 8282
    if (!zoom || zoom >= 15) {
      return;
    }

    // Reset animation state for new position
    animationRef.current.startTime = null;
    animationRef.current.isInitialAnimation = true;

    const coordinate = transform(
      [longitude, latitude],
      dataProjection,
      displayProjection
    );

    // Create feature and layer
    const crosshairFeature = new Feature<Point>(new Point(coordinate));
    const crosshairSource = new VectorSource<Feature<Point>>();
    crosshairSource.addFeature(crosshairFeature);

    const crosshairLayer = new VectorLayer<Feature<Point>>({
      source: crosshairSource,
    });
    crosshairLayer.setZIndex(9999);
    map.addLayer(crosshairLayer);

    // Get primary color from CSS
    const color = getPrimaryColor();

    // Animation settings
    const springDuration = 350;
    const breatheCycleDuration = 3000; // 3 seconds per breath cycle (slower)
    const baseSize = 20;
    const cornerLength = 5;
    const strokeWidth = 3;

    /**
     * Draw the corner brackets at a given scale and opacity
     */
    const createCrosshairStyles = (scale: number, opacity: number): Style[] => {
      const size = baseSize * scale;
      const halfSize = size / 2;
      const corner = cornerLength * scale;

      const style = new Style({
        renderer: (coordinates, state) => {
          const ctx = state.context;
          const [x, y] = coordinates as number[];

          ctx.save();
          ctx.strokeStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`;
          ctx.lineWidth = strokeWidth;
          ctx.lineCap = 'square';

          // Top-left corner
          ctx.beginPath();
          ctx.moveTo(x - halfSize + corner, y - halfSize);
          ctx.lineTo(x - halfSize, y - halfSize);
          ctx.lineTo(x - halfSize, y - halfSize + corner);
          ctx.stroke();

          // Top-right corner
          ctx.beginPath();
          ctx.moveTo(x + halfSize - corner, y - halfSize);
          ctx.lineTo(x + halfSize, y - halfSize);
          ctx.lineTo(x + halfSize, y - halfSize + corner);
          ctx.stroke();

          // Bottom-left corner
          ctx.beginPath();
          ctx.moveTo(x - halfSize + corner, y + halfSize);
          ctx.lineTo(x - halfSize, y + halfSize);
          ctx.lineTo(x - halfSize, y + halfSize - corner);
          ctx.stroke();

          // Bottom-right corner
          ctx.beginPath();
          ctx.moveTo(x + halfSize - corner, y + halfSize);
          ctx.lineTo(x + halfSize, y + halfSize);
          ctx.lineTo(x + halfSize, y + halfSize - corner);
          ctx.stroke();

          ctx.restore();
        },
      });

      return [style];
    };

    // Animation loop using postrender
    const animate = () => {
      const now = Date.now();
      if (animationRef.current.startTime === null) {
        animationRef.current.startTime = now;
      }

      const elapsed = now - animationRef.current.startTime;

      let scale: number;
      let opacity: number;

      if (animationRef.current.isInitialAnimation && elapsed < springDuration) {
        // Initial spring-in animation
        const progress = elapsed / springDuration;
        const easedProgress = springEase(progress);

        scale = 0.5 + 0.5 * easedProgress;
        opacity = Math.min(1, easedProgress * 1.5);
      } else {
        // Transition to breathing pulse
        if (animationRef.current.isInitialAnimation) {
          animationRef.current.isInitialAnimation = false;
          animationRef.current.startTime = now; // Reset for pulse timing
        }

        const breatheElapsed = now - animationRef.current.startTime!;
        const breatheProgress = (breatheElapsed % breatheCycleDuration) / breatheCycleDuration;

        scale = breathePulse(breatheProgress);
        opacity = 1;
      }

      crosshairLayer.setStyle(createCrosshairStyles(scale, opacity));
      map.render();
    };

    const listenerKey = map.on('postrender', animate);
    map.render(); // Trigger initial render

    return () => {
      unByKey(listenerKey);
      map.removeLayer(crosshairLayer);
    };
  }, [map, latitude, longitude, displayProjection, dataProjection]);
};
